import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, CheckCircle2, User, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `/api`;

const ResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchScreening = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/screening/${id}`, { withCredentials: true });
      setScreening(response.data);
    } catch (error) {
      toast.error('Failed to load screening results');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const getImpactColor = (impact) => {
    if (impact === 'significant') return '#EF4444';
    if (impact === 'moderate') return '#F59E0B';
    return '#10B981';
  };

  useEffect(() => {
    fetchScreening();
  }, [fetchScreening]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generatePDF = () => {
    if (!screening) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('FrameEd Referrals — Screening Report', margin, yPosition);
    yPosition += 15;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${formatDate(screening.created_at)}`, margin, yPosition);
    yPosition += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Student Information', margin, yPosition);
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Student Name: ${screening.student_name}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Teacher: ${screening.teacher_name}`, margin, yPosition);
    yPosition += 12;

    // Referral Decision - Prominent
    if (screening.referral_type === 'special_education') {
      doc.setFillColor(239, 68, 68);
    } else {
      doc.setFillColor(16, 185, 129);
    }
    doc.rect(margin - 5, yPosition - 5, pageWidth - 2 * margin + 10, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('REFERRAL DECISION', margin, yPosition);
    yPosition += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const referralLines = doc.splitTextToSize(screening.referral_message, pageWidth - 2 * margin);
    doc.text(referralLines, margin, yPosition);
    yPosition += referralLines.length * 5 + 10;
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Primary Concern', margin, yPosition);
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Type: ${Array.isArray(screening.concern_type) ? screening.concern_type.join(', ') : screening.concern_type}`, margin, yPosition);
    yPosition += 6;
    if (screening.concern_details) {
      const concernLines = doc.splitTextToSize(`Details: ${screening.concern_details}`, pageWidth - 2 * margin);
      doc.text(concernLines, margin, yPosition);
      yPosition += concernLines.length * 5 + 8;
    } else {
      yPosition += 8;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Impact Assessment', margin, yPosition);
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    doc.text(`Classroom Grades: ${screening.classroom_grades_impact.charAt(0).toUpperCase() + screening.classroom_grades_impact.slice(1)}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Testing Results: ${screening.testing_results_impact.charAt(0).toUpperCase() + screening.testing_results_impact.slice(1)}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Social Communication: ${screening.social_communication_impact.charAt(0).toUpperCase() + screening.social_communication_impact.slice(1)}`, margin, yPosition);
    yPosition += 8;
    doc.setFont('helvetica', 'bold');
    doc.text(`Significant Impact Areas: ${screening.significant_count} of 3`, margin, yPosition);
    yPosition += 15;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Recommendations', margin, yPosition);
    yPosition += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    screening.recommendations.forEach((rec, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      const recLines = doc.splitTextToSize(`${index + 1}. ${rec}`, pageWidth - 2 * margin - 5);
      doc.text(recLines, margin, yPosition);
      yPosition += recLines.length * 5 + 4;
    });

    doc.save(`screening-${screening.student_name}-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fe-bg)' }}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-[#595959]">Loading screening results...</p>
        </div>
      </div>
    );
  }

  if (!screening) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fe-bg)' }}>
        <div className="text-center">
          <FileText className="w-16 h-16 text-[#A3A3A3] mx-auto mb-4" />
          <p className="text-[#595959]">Screening not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-[#2C4C3B] hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <button
            data-testid="back-to-dashboard-from-results-button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <button
            data-testid="download-pdf-button"
            onClick={generatePDF}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden animate-fade-in">
          <div className="bg-[#2C4C3B] p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Referral Complete</h1>
                <p className="text-white/80">Comprehensive assessment results and recommendations</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Referral Decision - Prominent */}
            <div className={`rounded-2xl p-6 border-2 ${
              screening.referral_type === 'special_education' 
                ? 'bg-red-50 border-red-500' 
                : 'bg-emerald-50 border-emerald-500'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  screening.referral_type === 'special_education' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-emerald-500 text-white'
                }`}>
                  {screening.referral_type === 'special_education' ? (
                    <FileText className="w-6 h-6" />
                  ) : (
                    <CheckCircle2 className="w-6 h-6" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className={`text-xl font-bold mb-2 uppercase tracking-wide ${
                    screening.referral_type === 'special_education' 
                      ? 'text-red-700' 
                      : 'text-emerald-700'
                  }`}>
                    Referral Decision
                  </h2>
                  <p className="text-base text-gray-800 leading-relaxed font-medium">
                    {screening.referral_message}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-[#2C4C3B]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-1">Student</p>
                  <p className="text-lg font-semibold text-gray-900">{screening.student_name}</p>
                  {screening.grade_level && <p className="text-sm text-gray-600">Grade: {screening.grade_level}</p>}
                  <p className="text-sm text-gray-600">Teacher: {screening.teacher_email || screening.teacher_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4A261]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-[#F4A261]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-1">Date</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(screening.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Primary Concern</h2>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Type</p>
                <p className="text-lg font-semibold text-gray-900 capitalize mb-4">
                  {Array.isArray(screening.concern_type) ? screening.concern_type.join(', ') : screening.concern_type}
                </p>
                {screening.concern_details && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Details</p>
                    <p className="text-base text-gray-700 leading-relaxed">{screening.concern_details}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Impact Assessment</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Classroom Grades</p>
                  <p className="text-lg font-semibold capitalize" style={{ color: getImpactColor(screening.classroom_grades_impact) }}>
                    {screening.classroom_grades_impact}
                  </p>
                  {screening.classroom_grades_details && (
                    <p className="text-sm text-gray-600 mt-2">{screening.classroom_grades_details}</p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Testing Results</p>
                  <p className="text-lg font-semibold capitalize" style={{ color: getImpactColor(screening.testing_results_impact) }}>
                    {screening.testing_results_impact}
                  </p>
                  {screening.testing_results_details && (
                    <p className="text-sm text-gray-600 mt-2">{screening.testing_results_details}</p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Social Communication</p>
                  <p className="text-lg font-semibold capitalize" style={{ color: getImpactColor(screening.social_communication_impact) }}>
                    {screening.social_communication_impact}
                  </p>
                  {screening.social_communication_details && (
                    <p className="text-sm text-gray-600 mt-2">{screening.social_communication_details}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Recommendations</h2>
              <div className="space-y-4">
                {screening.recommendations.map((rec, index) => (
                  <div key={`rec-${screening.id}-${index}`} className="flex gap-4 bg-[#E2DACD]/10 rounded-2xl p-5">
                    <div className="w-8 h-8 bg-[#2C4C3B] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-base text-gray-800 leading-relaxed flex-1">{rec}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FEFAE0] rounded-2xl p-6 border border-[#E2DACD]/30">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>Note:</strong> This tool is designed to identify potential areas of concern and provide initial recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;